<template>
  <v-app>
    <div>
      <v-card class="px-5 pb-5">
        <v-card-title class="pl-0 pr-0 align-start">
          <v-col cols="2" class="pl-0">
            <router-link class="mr-3 text-decoration-none" icon to="/gallery">
              <v-icon>mdi-arrow-left</v-icon>
            </router-link>

            {{ $t('Gallery.CameraRoll') }}
          </v-col>

          <v-col cols="10" class="pl-0">
            <v-flex lg12 md12 xs12 class="pl-0">
              <v-row ma-5 class="justify-content-end">
                <v-col cols="4">
                  <v-menu v-model="picker.start" :close-on-content-click="false" :nudge-right="40"
                          transition="scale-transition" offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="formattedStartDate" :label="$t('GeneralTranslations.StartDate')" prepend-icon="mdi-calendar"
                                    readonly v-bind="attrs" v-on="on"></v-text-field>
                    </template>
                    <v-date-picker :locale="$i18n.locale" v-model="startDate" @input="picker.start = false">
                    </v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="4">
                  <v-menu v-model="picker.end" :close-on-content-click="false" :nudge-right="40" transition="scale-transition"
                          offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="formattedEndDate" :label="$t('GeneralTranslations.EndDate')" prepend-icon="mdi-calendar"
                                    readonly v-bind="attrs" v-on="on">
                      </v-text-field>
                    </template>
                    <v-date-picker :locale="$i18n.locale" v-model="endDate" @input="picker.end = false">
                    </v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="4" class="justify-content-end d-flex pr-0">
                  <div class="text-center mr-3" v-if="selectedPhotos.length">
                    <v-btn icon large @click.stop="deletePhoto()" :loading="loading" color="primary">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </div>

                  <v-btn class="primary mr-3" @click="getPhotos">{{ $t('GeneralTranslations.Update') }}</v-btn>

                  <div>
                    <v-btn class="primary" :loading="imgLoading" @click="$refs.inputUploadImage.click()">
                      {{ $t('Gallery.UploadImage') }}
                    </v-btn>
                    <input v-show="false" ref="inputUploadImage" id="fileInput" type="file" @change="saveEventImage" accept="image/png, image/jpeg, image/jpg, image/webp" multiple/>
                  </div>
                </v-col>
              </v-row>
            </v-flex>
          </v-col>
        </v-card-title>

        <v-row>
          <v-col v-for="photo in photos" :key="photo.id" class="d-flex child-flex" cols="2">
            <v-card>
              <v-img
                v-if="photo.image"
                :src="photo.image"
                :key="photo.id"
                :lazy-src="photo.src"
                aspect-ratio="1"
                class="grey lighten-2"
                v-once
                @click="openDialog($event, photo)"
                height="200"
              >
                <v-checkbox
                  class="gallery-item-checkbox pa-3"
                  @change="onPhotoSelect($event, photo)"
                ></v-checkbox>
              </v-img>

              <div v-if="photo.video" class="gallery-video" @click="openDialog($event, photo)">
                <v-checkbox
                  class="gallery-item-checkbox pa-3"
                  @change="onPhotoSelect($event, photo)"
                ></v-checkbox>

                <video
                  height="200"
                  :src="photo.video"
                >
                  {{ $t('GeneralTranslations.YourBrowserDoesNotSupportTheVideoTag') }}.
                </video>

                <div class="gallery-video-play">
                  <v-icon>
                    mdi-play
                  </v-icon>
                </div>
              </div>

              <v-card-title class="">
                <v-col>
                  <v-row>
                    <div v-if="photo?.isUploaded" style="word-break: break-word;">
                      <b class="mr-1">{{ $t('Gallery.AddedBy') }}:</b> {{ photo.sentBy }}
                    </div>

                    <div v-else style="word-break: break-word;">
                      <b class="mr-1">{{ $t('Gallery.TakenBy') }}:</b> {{ photo.sentBy }}
                    </div>
                  </v-row>

                  <v-row style="word-break: break-word;">
                    <b class="mr-1">{{ $t('Gallery.Date') }}: </b>{{photo.createdAt | localizedDate}}
                  </v-row>
                </v-col>
              </v-card-title>
            </v-card>
          </v-col>
        </v-row>
      </v-card>

      <ConfirmDlg ref="confirmationModal"></ConfirmDlg>

      <SharePhotoDlg ref="sharePhotoDlg"></SharePhotoDlg>

      <GalleryShareDlg
        ref="galleryShareDlg"
        @getPhotos="getFacilityPhotos"
        @shareMediaByEmail="shareMediaByEmail"
      />
    </div>
  </v-app>
</template>

<script>
import { mapState } from 'vuex';
import { serializer } from '../../../../api/shared/serializer';
import firebase from 'firebase/app';
import {deleteFileByUrl, uploadImageAsync, optimizePhotos} from '@/utils/upload-image';
import moment from 'moment';
import ConfirmDlg from '@/components/popup/confirm-dialog/ConfirmDlg.vue';
import SharePhotoDlg from "@/views/staff/gallery/SharePhotoDlg/SharePhotoDlg.vue";
import {v4 as uuidv4} from "uuid";
import GalleryShareDlg from "@/views/staff/gallery/GalleryShareDlg/GalleryShareDlg.vue";
import {waitResizedImage} from "@/utils/wait-resized-image";
import { UserTypeEnum } from '@/enums/UserTypeEnum';
import { AuditLogsTier1 as Tier1, AuditLogsTier2 as Tier2, AuditLogsTier3 as Tier3 } from '@/enums/AuditLogs';

export default {
  name: 'gallery',
  components: {
    SharePhotoDlg,
    ConfirmDlg,
    GalleryShareDlg
  },
  data() {
    return {
      picker: {
        start: false,
        end: false,
      },
      startDate: moment().subtract(1, 'month').toISOString().substring(0, 10),
      endDate: moment().toISOString().substring(0, 10),
      sharing: {
        title: '',
        description: '',
        quote: 'u',
        hashtags: '',
      },
      search: '',
      loading: false,
      selectedPhotos: [],
      imgLoading: false
    };
  },

  computed: {
    ...mapState({
      facility: state => state.facilityModule.selectedFacility,
      staffUser: state => state.authModule.user,
      p2: state => state.galleryModule.photos,
      photos: state =>
          serializer.transformFields(state.galleryModule.photos, {
            src: image => image,
            description: () =>
                'Star Night Sky Ravine by Mark Basarab, from Unsplash.',
            thumbnailWidth: () => '220px',
            alt: alt => 'no image',
          }),
    }),
    formattedStartDate() {
      return moment(this.startDate).format(this.facility.dateFormat);
    },
    formattedEndDate() {
      return moment(this.endDate).format(this.facility.dateFormat);
    },

    categoryFilter() {
      return [
        {
          id: '',
          name: 'All',
        },
        // ...this.categories,
      ];
    },
  },

  created() {
    this.sharing.title = this.$i18n.messages[this.$i18n.locale]['Gallery.CheckOutOurLatestPhoto']
    this.$store.dispatch('getFacilityPhotos', {
      startDate: moment(this.startDate).toDate(),
      endDate: moment(this.endDate).toDate(),
    });
  },

  methods: {
    handleClickContentItem(row) {
      this.$router.push('/gallery/' + row.id);
    },

    getPhotos() {
      this.$store.dispatch('getFacilityPhotos', {
        startDate: moment(this.startDate).toDate(),
        endDate: moment(this.endDate).toDate(),
      });
    },

    onPhotoSelect(isSelected, photo) {
      if (isSelected) {
        this.selectedPhotos.push(photo)
      }
      else {
        this.selectedPhotos = this.selectedPhotos.filter(x => x.id != photo.id)
      }
    },

    async deletePhoto() {

      if (this.selectedPhotos.length <= 0) return

      this.$refs.confirmationModal
          .open(null,
              this.selectedPhotos.length > 1 ? this.$i18n.messages[this.$i18n.locale]['Gallery.AreYouSureYouWantToDeleteThesePicturesMessage'] :
                  this.$i18n.messages[this.$i18n.locale]['Gallery.AreYouSureYouWantToDeleteThisPictureMessage'], false)
          .then(async (response) => {
            if (response && response.agree) {
              await this.deletePhotos(this.selectedPhotos)
            }
          });
    },

    async deletePhotos(photos) {
      this.loading = true;

      for (const photo of photos) {
        const photoRef = await firebase.firestore().collection('photos').doc(photo.id);

        await photoRef.update({
          deletedById: this.$store.state.authModule.user.id,
          deletedByType: UserTypeEnum.Staff,
          typesOfRecordChanged: [`${Tier1.ContentManager}: ${Tier2.Gallery}: ${Tier3.DeleteImage}`]
        });

        await photoRef.delete()
            .then(async () => {
              await deleteFileByUrl(photo.image)
                  .then(async () => {
                    await this.$store
                        .dispatch('getFacilityPhotos', {
                          startDate: moment(this.startDate).toDate(),
                          endDate: moment(this.endDate).toDate(),
                        })
                        .then(async () => {
                          const str = photo.image;
                          const from = 0;
                          const to = str.search('alt') - 1;
                          const first = str.substring(from, to) + '_200x200';
                          const second = str.substring(to, str.length);
                          const url = first + second;
                          await deleteFileByUrl(url);

                          this.selectedPhotos = this.selectedPhotos.filter(x => x.id != photo.id)

                        });
                  })
                  .catch(() => {
                    this.$toast.open({
                      message: this.$i18n.messages[this.$i18n.locale]['Gallery.PhotoCannotBeDeletedMessage'],
                      type: 'error',
                    });
                  });
            })
            .catch(() => {
              this.$toast.open({
                message: this.$i18n.messages[this.$i18n.locale]['Gallery.PhotoCannotBeDeletedMessage'],
                type: 'error',
              });
            });
      }

      this.loading = false;

      this.$toast.open({
        message: photos.length > 1? this.$i18n.messages[this.$i18n.locale]['Gallery.PhotosSuccessfullyDeleted'] : this.$i18n.messages[this.$i18n.locale]['Gallery.PhotoSuccessfullyDeleted'],
        type: 'success',
      });

    },

    openDialog(event, photo) {
      if (event.target.className.includes('selection-controls')) return;

      this.$refs.galleryShareDlg.open(photo);
    },

    shareMediaByEmail(media) {
      this.$refs.sharePhotoDlg.open(media);
    },

    async saveEventImage(e) {
      this.imgLoading = true;
      const files = e.target.files;

      if (files.length) {
        const uploadPromises = [];

        for (let i = 0; i < files.length; i++) {
          const file = await optimizePhotos(files[i], 'background-image');
          const path = `${this.facility.id}/photos/staff/${uuidv4()}`;

          const uploadPromise = uploadImageAsync({ path, file }).then(async (storage) => {
            const url = await storage.ref.getDownloadURL();
            const id = String(new Date().getTime());

            const resizedImageRef = await firebase.storage().ref().child(`${path}_200x200`);
            const resizedImageUrl = await waitResizedImage(10, resizedImageRef);

            const data = {
              createdAt: new Date().getTime(),
              facilityId: this.facility.id,
              image: url,
              thumbnailUrl: resizedImageUrl,
              isStaff: true,
              isVisibleForStaffGallery: false,
              sentBy: this.staffUser.name,
              sentById: this.staffUser.id,
              residentId: null,
              isUploaded: true,
              createdById: this.staffUser.id,
              createdByType: UserTypeEnum.Staff,
              typesOfRecordChanged: [`${Tier1.ContentManager}: ${Tier2.Gallery}: ${Tier3.AddImage}`]
            };

            await firebase
                .firestore()
                .collection("photos")
                .doc(id)
                .set(data);

            return true;
          });

          uploadPromises.push(uploadPromise);
        }

        try {
          await Promise.all(uploadPromises).then(() => {
            this.getPhotos();
            this.$toast.open({
              message: files.length > 1 ? this.$t('Gallery.SuccessUploadingImages') : this.$t('Gallery.SuccessUploadingImage'),
              type: "success"
            });
            this.clearInputFile();
          })
        } catch (error) {
          console.error("Error uploading images:", error);
        } finally {
          this.imgLoading = false;
        }
      }
    },
    clearInputFile() {
      const fileInput = document.getElementById('fileInput');
      fileInput.value = "";
    },
    getFacilityPhotos(photo) {
      this.selectedPhotos = this.selectedPhotos.filter(x => x.id != photo?.id);

      this.getPhotos();
    }
  },
};
</script>
<style src="./GalleryCameraRoll.scss" lang="scss" scoped></style>
